import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  SafeAreaView,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useCssStyle } from '../../customHook/getCssStyle';
import CustomPressable from '../../components/customPressable/customPressable';
import { ImageComp } from '../../components/Image/Image';
import { CROSS, MINUS, PLUS } from '../../assets';
import CustomCheckBoxWithProps from '../../components/checkBox/customCheckBox';
import { CustomModal } from '../../components/customModal';
import RenderHTML from 'react-native-render-html';
import { getCleanUrl } from '../../utils/Helper';

/**
 * ItemAddOnsModal — bottom-sheet modal for items with direct (flat) add-ons.
 * Shows item details, optional flat add-ons (checkboxes), cooking instructions,
 * and an "Add to Order" button.
 */
const ItemAddOnsModal = (props) => {
  const {
    selectedStoreItem = {},
    addonsList = {},
    configs = {},
    handleAddToCart,
    handleClose,
  } = props || {};

  const getStyles = useCssStyle();
  const { width, height } = useWindowDimensions();
  const isLandscape = width > height;

  const { currencyDetails } = useSelector((state) => state.deviceConfigSlice);
  const currencyCode = currencyDetails?.code || '';

  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [cookingInstructions, setCookingInstructions] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showFullDesc, setShowFullDesc] = useState(false);

  // Resolve flat addons from addOnItem IDs
  const itemAddons = useMemo(() => {
    return (selectedStoreItem.addOnItem || [])
      .map(id => addonsList[id] || addonsList[String(id)])
      .filter(Boolean)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [selectedStoreItem.addOnItem, addonsList]);

  const toggleAddOn = (addOnInfo) => {
    setSelectedAddOns(prev => {
      if (prev.some(a => a.id === addOnInfo.id)) {
        return prev.filter(a => a.id !== addOnInfo.id);
      }
      return [...prev, addOnInfo].sort((a, b) => a.id - b.id);
    });
  };

  const totalPrice = useMemo(() => {
    const addonTotal = selectedAddOns.reduce((s, a) => s + parseFloat(a.price || 0), 0);
    return ((parseFloat(selectedStoreItem.itemPrice || 0) + addonTotal) * quantity).toFixed(2);
  }, [selectedAddOns, selectedStoreItem.itemPrice, quantity]);

  const handleAddItem = () => {
    handleAddToCart(
      { ...selectedStoreItem, quantity, ...(cookingInstructions && { cookingInstructions }) },
      selectedAddOns,
      true,
    );
    handleClose();
  };

  const descriptionStyle = useMemo(() => {
    const s = getStyles(['list_itemContainer_textContainer_subtitle']);
    return Array.isArray(s) ? Object.assign({}, ...s) : s || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemImage = selectedStoreItem.storeItemImages?.find(i => i.imageType === 'LARGE')?.imagePath
    || selectedStoreItem.storeItemImages?.[0]?.imagePath;
  const cleanImageUrl = itemImage ? getCleanUrl(itemImage) : null;

  return (
    <CustomModal onRequestClose={handleClose} landscapeStyle={getStyles(["hcommerce_modal_landscape"])}>
      {/* Close button above the sheet*/}
      <View style={getStyles(['hcommerce_modal_closeAboveContainer'])} id='hcommerce_modal_closeAboveContainer'>
        <CustomPressable onPress={handleClose} style={getStyles(['hcommerce_modal_closeAboveBtn'])} id="hcommerce_modal_closeAboveBtn">
          <ImageComp source={CROSS} style={getStyles(['hcommerce_modal_closeAboveIcon'])} id="hcommerce_modal_closeAboveIcon" />
        </CustomPressable>
      </View>

      <View style={[getStyles([{ maxHeight: isLandscape ? '100%' : height * 0.92 }, isLandscape ? 'hcommerce_modal_sheet_landscape' : 'hcommerce_modal_sheet'])]} id='hcommerce_modal_sheet'>
        {/* Drag indicator — portrait only */}
        {!isLandscape && (
          <View style={getStyles(['hcommerce_modal_dragIndicatorWrap'])} id='hcommerce_modal_dragIndicatorWrap'>
            <View style={getStyles(['hcommerce_modal_dragIndicator'])} id='hcommerce_modal_dragIndicator' />
          </View>
        )}

        {/* Image — left panel in landscape */}
        {cleanImageUrl && isLandscape && (
          <View style={getStyles(['hcommerce_modal_imageContainer_landscape'])} id='hcommerce_modal_imageContainer_landscape'>
            <ImageComp source={cleanImageUrl} style={getStyles(['hcommerce_modal_image'])} resizeMode="cover" id='hcommerce_modal_image' />
          </View>
        )}

        {/* Right column: scroll + footer (full-width in portrait, right panel in landscape) */}
        <View style={getStyles(['hcommerce_modal_rightColumn'])} id='hcommerce_modal_rightColumn'>
        {/* Scrollable content */}
        <ScrollView
          style={getStyles(['hcommerce_modal_scroll'])}
          id='hcommerce_modal_scroll'
          contentContainerStyle={getStyles(['hcommerce_modal_scrollContentFlat'])}
          contentContainerId='hcommerce_modal_scrollContentFlat'
          showsVerticalScrollIndicator={false}
        >
          {/* Item image — inside scroll in portrait only */}
          {cleanImageUrl && !isLandscape && (
            <View style={getStyles(['hcommerce_modal_imageContainer'])} id='hcommerce_modal_imageContainer'>
              <ImageComp source={cleanImageUrl} style={getStyles(['hcommerce_modal_image'])} resizeMode="cover" id='hcommerce_modal_image' />
            </View>
          )}

          <View style={getStyles(['hcommerce_modal_contentContainer'])} id='hcommerce_modal_contentContainer' >
          {/* Item details — centered */}
          <View style={getStyles(['hcommerce_modal_itemInfoFlat'])} id='hcommerce_modal_itemInfoFlat'>
            <Text style={[getStyles(['AddOnsModalContent_childcontainer_header_container_title']), getStyles(['hcommerce_modal_itemName'])]} id='hcommerce_modal_itemName'>
              {selectedStoreItem.itemName}
            </Text>
            <Text style={[getStyles(['AddOnsModalContent_childcontainer_header_container_subtitle']), getStyles(['hcommerce_modal_itemPrice'])]} id='hcommerce_modal_itemPrice'>
              {currencyCode}{selectedStoreItem.itemPrice}
            </Text>
            {selectedStoreItem.itemDescription ? (
              <>
                <View style={showFullDesc ? null : getStyles(['hcommerce_modal_descClamp'])} id='hcommerce_modal_descClamp'>
                  <RenderHTML
                    contentWidth={width - 48}
                    source={{ html: selectedStoreItem.itemDescription }}
                    baseStyle={{ ...descriptionStyle, textAlign: 'center' }}
                    id='hcommerce_modal_descRenderHTML'
                  />
                </View>
                <TouchableOpacity style={getStyles(['hcommerce_modal_showMoreBtn'])} onPress={() => setShowFullDesc(v => !v)} activeOpacity={0.7} id='hcommerce_modal_showMoreBtn'>
                  <Text style={getStyles(['hcommerce_modal_showMoreBtnText'])} id='hcommerce_modal_showMoreBtnText'>
                    {showFullDesc ? 'Show less' : 'Show more'}
                  </Text>
                </TouchableOpacity>
              </>
            ) : null}
          </View>

          {/* Flat add-ons */}
          {itemAddons.length > 0 && (
            <View style={getStyles(['hcommerce_flat_groupSection'])} id='hcommerce_flat_addons_groupSection'>
              <View style={getStyles(['hcommerce_flat_groupHeader'])} id='hcommerce_flat_groupHeader'>
                <Text style={getStyles(['AddOnGrouping_drawer_header_title'])} id='hcommerce_flat_groupHeader_title'>Add-ons</Text>
                <Text style={getStyles(['AddOnGrouping_drawer_header_subtitle'])} id='hcommerce_flat_groupHeader_subtitle'>Optional</Text>
              </View>
              {itemAddons.map(addon => {
                const isSelected = selectedAddOns.some(a => a.id === addon.id);
                const hasPrice = parseFloat(addon.price || 0) > 0;
                return (
                  <TouchableOpacity
                    key={addon.id}
                    style={getStyles(['hcommerce_flat_addonRow'])}
                    onPress={() => toggleAddOn(addon)}
                    activeOpacity={0.8}
                    id={`hcommerce_flat_addonRow_${addon.id}`}
                  >
                    <View style={getStyles(['hcommerce_flat_addonRowLeft'])} id={`hcommerce_flat_addonRowLeft_${addon.id}`}>
                      {addon.largeImage && (
                        <View style={getStyles(['hcommerce_flat_addonImage'])} id={`hcommerce_flat_addonImage_${addon.id}`}>
                          <ImageComp
                            source={getCleanUrl(addon.largeImage)}
                            style={{ width: '100%', height: '100%' }}
                            id={`hcommerce_flat_addonImageComp_${addon.id}`}
                          />
                        </View>
                      )}
                      <View style={getStyles(['hcommerce_flat_addonText'])} id={`hcommerce_flat_addonText_${addon.id}`}>
                        <Text style={getStyles(['list_itemContainer_textContainer_title'])} numberOfLines={2} id={`hcommerce_flat_addonName_${addon.id}`}>
                          {addon.name}
                        </Text>
                        {addon.description ? (
                          <Text style={getStyles(['list_itemContainer_textContainer_subtitle'])} numberOfLines={2} id={`hcommerce_flat_addonDesc_${addon.id}`}>
                            {addon.description}
                          </Text>
                        ) : null}
                        {hasPrice && (
                          <Text style={getStyles(['AddOnsModalContent_addons_itemContainer_textContainer_price'])} id={`hcommerce_flat_addonPrice_${addon.id}`}>
                            +{currencyCode}{addon.price}
                          </Text>
                        )}
                      </View>
                    </View>
                    <CustomCheckBoxWithProps
                      value={isSelected}
                      onValueChange={() => toggleAddOn(addon)}
                      id={`hcommerce_flat_addonCheckbox_${addon.id}`}
                    />
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {/* Cooking instructions */}
          <View style={getStyles(['hcommerce_flat_groupSection'])} id='hcommerce_flat_instructions_groupSection'>
            <Text style={getStyles(['AddOnGrouping_drawer_header_title'])} id='hcommerce_flat_instructions_title'>Special Instructions</Text>
            <Text style={getStyles(['AddOnGrouping_drawer_header_subtitle'])} id='hcommerce_flat_instructions_subtitle'>Optional</Text>
            <TextInput
              style={getStyles(['hcommerce_modal_instructionsInput'])}
              placeholder="e.g. No onions, extra sauce..."
              placeholderTextColor="rgba(0,0,0,0.4)"
              value={cookingInstructions}
              onChangeText={setCookingInstructions}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
              id='hcommerce_modal_instructionsInput'
            />
          </View>

          <View style={getStyles(['hcommerce_modal_bottomPad'])} id='hcommerce_modal_bottomPad' />

          </View>
        </ScrollView>

        {/* Footer */}
        <SafeAreaView edges={['bottom']} id='hcommerce_modal_safeAreaView'>
          <View style={getStyles(['hcommerce_modal_footerFlat'])} id='hcommerce_modal_footerFlat'>
            {!configs.disableAddRemoveItemsButtons && (
              <View style={getStyles(['hcommerce_modal_footerRowFlat'])} id='hcommerce_modal_footerRowFlat'>
                <View style={getStyles(['hcommerce_modal_qtyBlockFlat'])} id='hcommerce_modal_qtyBlockFlat'>
                  <CustomPressable
                    style={getStyles(['hcommerce_modal_qtyBtnFlat'])}
                    onPress={() => setQuantity(q => Math.max(1, q - 1))}
                    id='hcommerce_modal_qtyBtnMinus'
                  >
                    <ImageComp source={MINUS} style={getStyles(['hcommerce_modal_qtyIconFlat'])} id='hcommerce_modal_qtyIconMinus' />
                  </CustomPressable>
                  <Text style={getStyles(['additem_controls_qty_text'])} id='hcommerce_modal_qtyText'>{quantity}</Text>
                  <CustomPressable
                    style={getStyles(['hcommerce_modal_qtyBtnFlat'])}
                    onPress={() => setQuantity(q => q + 1)}
                    id='hcommerce_modal_qtyBtnPlus'
                  >
                    <ImageComp source={PLUS} style={getStyles(['hcommerce_modal_qtyIconFlat'])} id='hcommerce_modal_qtyIconPlus' />
                  </CustomPressable>
                </View>
                <CustomPressable style={[getStyles(['primaryBtn']), getStyles(['hcommerce_modal_addBtnFlex'])]} onPress={handleAddItem} id="primaryBtn">
                  <Text style={getStyles(['primaryBtnTxt'])} id='primaryBtnTxt'>
                    Add to Order · {currencyCode}{totalPrice}
                  </Text>
                </CustomPressable>
              </View>
            )}
          </View>
        </SafeAreaView>
        </View>{/* hcommerce_modal_rightColumn */}
      </View>
    </CustomModal>
  );
};

export default ItemAddOnsModal;